import { Component } from 'react'
import { IClientUID } from "../../../api/clients"
import { CycleAction, CycleTask, GpioLevel, GpioType, SetIoLevelRequest } from '../../../api/0x0004/filter-filler'
import { ErrorAlert } from './components/ErrorAlert'

const DELAY_BETWEEN_ACTIVATION = 500

type Props = {
    clientId: IClientUID
}

type State = {
    gpio: Record<GpioType, GpioLevel>
    gpioDelay: Record<GpioType, number>
    error?: string
}

class TestTab extends Component<Props, State> {
    readonly state: State = this.getInitialState()

    private getInitialState() {
        const state: State = {
            gpio: {} as State['gpio'],
            gpioDelay: {} as State['gpioDelay'],
        } as State
        for (const type of Object.values(GpioType)) {
            state.gpio[type as GpioType] = GpioLevel.Low
        }
        return state
    }

    private handleButtonRelease = async (type: GpioType) => {
        if (this.state.gpio[type] === GpioLevel.High) {
            SetIoLevelRequest(this.props.clientId, GpioType[type] as keyof typeof GpioType, GpioLevel.Low)
                .then(() => this.setState({ error: undefined }))
                .catch((err) => this.setState({ error: (err as Error).message }))

            this.setState((state) => ({ gpio: { ...state.gpio, [type]: GpioLevel.Low } }))
        }
    }

    private handleButtonPress = async (type: GpioType) => {
        if (this.state.gpio[type] === GpioLevel.Low) {
            const gpioDelay = this.state.gpioDelay[type]
            const isDelayed = gpioDelay && gpioDelay > Date.now()
            if (isDelayed) return

            SetIoLevelRequest(this.props.clientId, GpioType[type] as keyof typeof GpioType, GpioLevel.High)
                .then(() => this.setState({ error: undefined }))
                .catch((err) => this.setState({ error: (err as Error).message }))

            this.setState((state) => ({
                gpio: { ...state.gpio, [type]: GpioLevel.High },
                gpioDelay: { ...state.gpioDelay, [type]: Date.now() + DELAY_BETWEEN_ACTIVATION },
            }))
        }
    }

    private handleCycleTask = async (action: CycleAction) => {
        try {
            await CycleTask(this.props.clientId, action)
            this.setState({ error: undefined })
        } catch (error) {
            this.setState({ error: (error as Error).message })
        }
    }

    render() {
        const { error } = this.state
        return (
            <div dir="ltr" className="p-4">
                {error && <ErrorAlert message={error} />}
                <div className="w-full h-full flex flex-wrap justify-between items-stretch select-none pointer-events-none">
                    {Object.values(GpioType)
                        .filter((type) => typeof type === 'string')
                        .map((type) => this.renderToggleableGpioButton(type as unknown as GpioType))}
                    {Object.values(GpioType)
                        .filter((type) => typeof type === 'string')
                        .map((type) => this.renderHoldableGpioButton(type as unknown as GpioType))}
                    {this.renderCycleTaskButtons()}
                </div>
            </div>
        )
    }

    private renderCycleTaskButtons() {
        return (
            <>
                <button
                    className="mt-5 w-32 h-32 rounded-full shadow-lg text-white font-bold text-2xl pointer-events-auto bg-blue-500"
                    onClick={() => this.handleCycleTask(CycleAction.Start)}
                >
                    Start
                </button>
                <button
                    className="mt-5 w-32 h-32 rounded-full shadow-lg text-white font-bold text-2xl pointer-events-auto bg-yellow-500"
                    onClick={() => this.handleCycleTask(CycleAction.Stop)}
                >
                    Stop
                </button>
            </>
        )
    }

    private renderToggleableGpioButton(type: GpioType) {
        const level = this.state.gpio[type]
        return (
            <button
                key={type}
                onClick={() =>
                    level === GpioLevel.High ? this.handleButtonRelease(type) : this.handleButtonPress(type)
                }
                className={`mt-5 w-32 h-32 shadow-lg text-white font-bold text-2xl pointer-events-auto ${level === GpioLevel.High ? 'bg-green-500' : 'bg-red-500'
                    }`}
            >
                {type}
            </button>
        )
    }

    private renderHoldableGpioButton(type: GpioType) {
        const level = this.state.gpio[type]
        return (
            <button
                key={type}
                onTouchStart={() => this.handleButtonPress(type)}
                onTouchEnd={() => this.handleButtonRelease(type)}
                onMouseDown={() => this.handleButtonPress(type)}
                onMouseUp={() => this.handleButtonRelease(type)}
                onMouseLeave={() => this.handleButtonRelease(type)}
                className={`mt-5 w-32 h-32 rounded-full shadow-lg text-white font-bold text-2xl pointer-events-auto ${level === GpioLevel.High ? 'bg-green-500' : 'bg-red-500'
                    }`}
            >
                {type}
            </button>
        )
    }
}

export default TestTab